import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import LogoutButton from '../auth/LogoutButton';
import { useRoles } from '../auth/useRoles';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { accounts } = useMsal();
  const account = accounts[0];
  const { isAdmin } = useRoles();
  
  const userName = account?.name || account?.username || 'User';

  const navigation = [
    { name: 'Dashboard', href: '/', adminOnly: false },
    { name: 'Product Stacks', href: '/product-stacks', adminOnly: true },
    { name: 'Deployment Groups', href: '/admin/deployment-groups', adminOnly: true },
    { name: 'Release Flows', href: '/release-flows', adminOnly: false },
    { name: 'Releases', href: '/releases', adminOnly: false },
    ...(isAdmin ? [
    ] : []),
  ];

  const filteredNavigation = navigation.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0">
                <h1 className="text-xl font-bold text-gray-900">DevOps Core</h1>
              </Link>
              <nav className="hidden md:ml-6 md:flex md:space-x-8">
                {filteredNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 ${
                      location.pathname === item.href
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Welcome, {userName}</span>
                {isAdmin && (
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                    Admin
                  </span>
                )}
              </div>
              <LogoutButton />
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className="md:hidden">
          <div className="space-y-1 pb-3 pt-2">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block border-l-4 py-2 pl-3 pr-4 text-base font-medium ${
                  location.pathname === item.href
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}